import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { changeMetaThemeColor, requestIdleCallback } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerRowTop", "meganavDrawer"];

  /** @type {string[]} */
  #panelStack = ["main"];

  /** @type {number | null} */
  #drawerTimeout = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #groupResizeObserver = null;

  /** @type {MutationObserver | null} */
  #groupMutationObserver = null;

  /** @type {MediaQueryList | null} */
  #desktopMediaQuery = null;

  /** @type {HTMLElement | null} */
  #lastHoverTrigger = null;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  #offscreen = false;
  #lastScrollTop = 0;

  /** @type {number | null} */
  #timeout = null;

  #animationDelay = 150;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty("--header-height", `${height}px`);
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const { isIntersecting } = entry;

        if (alwaysSticky) {
          this.dataset.stickyState = isIntersecting ? "inactive" : "active";
          changeMetaThemeColor(this.refs.headerRowTop);
        } else {
          this.#offscreen =
            !isIntersecting || this.dataset.stickyState === "active";
        }
      },
      { threshold: alwaysSticky ? 1 : 0 },
    );

    this.#intersectionObserver.observe(this);
  };

  openMobileDrawer() {
    const drawer = this.refs.meganavDrawer;
    if (!drawer) return;
    if (this.#drawerTimeout) clearTimeout(this.#drawerTimeout);
    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    drawer.dataset.open = "";
    document.documentElement.setAttribute("scroll-lock", "");
    this.#setBackgroundInert(true);
    this.#panelStack = ["main"];
    this.#resetPanels();
    this.#activatePanel("main");
    this.querySelector("[data-meganav-drawer-open]")?.setAttribute(
      "aria-expanded",
      "true",
    );
    trapFocus(drawer);
    drawer.querySelector("[data-meganav-drawer-close]")?.focus();
  }

  closeMobileDrawer() {
    const drawer = this.refs.meganavDrawer;
    if (!drawer) return;
    delete drawer.dataset.open;
    document.documentElement.removeAttribute("scroll-lock");
    this.#setBackgroundInert(false);
    this.querySelector("[data-meganav-drawer-open]")?.setAttribute(
      "aria-expanded",
      "false",
    );
    removeTrapFocus();
    this.#previouslyFocused?.focus?.();
    this.#previouslyFocused = null;
    this.#drawerTimeout = setTimeout(() => {
      this.#resetPanels();
      this.#panelStack = ["main"];
      this.#activatePanel("main");
    }, 300);
  }

  /**
   * Mark every body child outside the drawer's ancestor chain as inert
   * while the mobile mega-nav is open, so screen-reader virtual cursors
   * and assistive tech can't escape the dialog.
   *
   * @param {boolean} inert
   */
  #setBackgroundInert(inert) {
    const drawer = this.refs.meganavDrawer;
    if (!drawer) return;
    const ancestors = new Set();
    let el = drawer.parentElement;
    while (el && el !== document.body) {
      ancestors.add(el);
      el = el.parentElement;
    }
    for (const child of document.body.children) {
      if (ancestors.has(child)) continue;
      if (inert) child.setAttribute("inert", "");
      else child.removeAttribute("inert");
    }
  }

  /** @param {Event} event */
  gotoPanel(event) {
    const trigger = event.target.closest("[data-meganav-goto]");
    const panelId = trigger?.dataset.meganavGoto;
    if (!panelId) return;
    const panel = this.#getPanel(panelId);
    if (!panel) return;
    this.#getPanel(this.#panelStack.at(-1))?.setAttribute("inert", "");
    panel.setAttribute("data-active", "");
    panel.removeAttribute("inert");
    this.#panelStack.push(panelId);
  }

  goBackPanel() {
    if (this.#panelStack.length <= 1) {
      this.closeMobileDrawer();
      return;
    }
    const popped = this.#panelStack.pop();
    const poppedPanel = this.#getPanel(popped);
    if (poppedPanel) {
      poppedPanel.removeAttribute("data-active");
      poppedPanel.setAttribute("inert", "");
    }
    this.#getPanel(this.#panelStack.at(-1))?.removeAttribute("inert");
  }

  /** @param {string | undefined} panelId */
  #getPanel(panelId) {
    if (!panelId) return null;
    return this.refs.meganavDrawer?.querySelector(
      `[data-meganav-panel="${panelId}"]`,
    );
  }

  #activatePanel(panelId) {
    const panel = this.#getPanel(panelId);
    if (!panel) return;
    panel.setAttribute("data-active", "");
    panel.removeAttribute("inert");
  }

  #resetPanels() {
    this.refs.meganavDrawer
      ?.querySelectorAll("[data-meganav-panel]")
      .forEach((p) => {
        p.removeAttribute("data-active");
        p.setAttribute("inert", "");
      });
  }

  /** @param {Event} event */
  toggleMeganav(event) {
    const trigger = event.target.closest("[data-meganav-target]");
    if (!trigger) return;
    const value = trigger.dataset.meganavTarget;
    const current = this.refs.headerRowTop.dataset.meganav;
    const next = current === value ? "" : value;
    this.#setMeganavState(next, next ? trigger.dataset.meganavBg : "");
  }

  closeMeganav() {
    this.#setMeganavState("", "");
  }

  /** @param {Event} event */
  updateHoverImage(event) {
    const trigger = event.target.closest("[data-meganav-hover-image]");
    if (!trigger || trigger === this.#lastHoverTrigger) return;
    this.#lastHoverTrigger = trigger;

    const panel = trigger.closest("[data-meganav-desktop]");
    const img = panel?.querySelector("[data-meganav-hover-img]");
    if (!img) return;

    const nextSrc = trigger.dataset.meganavHoverImage;
    if (img.getAttribute("src") !== nextSrc) img.src = nextSrc;
  }

  /** @param {KeyboardEvent} event */
  handleEscape(event) {
    if (event.key !== "Escape") return;

    const drawer = this.refs.meganavDrawer;
    if (drawer?.hasAttribute("data-open")) {
      if (this.#panelStack.length > 1) {
        this.goBackPanel();
      } else {
        this.closeMobileDrawer();
      }
      event.preventDefault();
      return;
    }

    if (this.refs.headerRowTop.dataset.meganav) {
      this.#setMeganavState("", "");
      event.preventDefault();
    }
  }

  #setMeganavState(value, bg) {
    this.refs.headerRowTop.dataset.meganav = value;
    this.refs.headerRowTop.style.backgroundColor = bg || "";

    if (value) {
      document.documentElement.setAttribute("scroll-lock", "");
    } else {
      document.documentElement.removeAttribute("scroll-lock");
    }

    this.querySelectorAll("[data-meganav-target]").forEach((btn) => {
      const active = btn.dataset.meganavTarget === value;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-expanded", active ? "true" : "false");
    });

    this.querySelectorAll("[data-meganav-desktop]").forEach((panel) => {
      panel.classList.toggle(
        "is-active",
        panel.dataset.meganavDesktop === value,
      );
    });

    if (!value) this.#lastHoverTrigger = null;
  }

  #handleBreakpointChange = () => {
    if (this.refs.meganavDrawer?.hasAttribute("data-open")) {
      this.closeMobileDrawer();
    }
    if (this.refs.headerRowTop.dataset.meganav) {
      this.#setMeganavState("", "");
    }
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute("data-animating");

      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      this.dataset.scrollDirection = "none";
      this.setAttribute("data-animating", "");

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = "idle";
        this.removeAttribute("data-animating");
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  #observeHeaderGroup() {
    const headerGroup = document.getElementById("header-group");
    if (!headerGroup) return;

    this.#groupResizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(this, headerGroup),
    );

    const observeSiblings = () => {
      for (const element of headerGroup.children) {
        if (element === this || !(element instanceof HTMLElement)) continue;
        this.#groupResizeObserver?.observe(element);
      }
    };

    observeSiblings();
    this.#groupMutationObserver = new MutationObserver(observeSiblings);
    this.#groupMutationObserver.observe(headerGroup, { childList: true });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    // Defer non-critical observers off the initial paint path.
    requestIdleCallback(() => {
      if (!this.isConnected) return;
      this.#observeHeaderGroup();
    });

    this.#desktopMediaQuery = window.matchMedia("(min-width: 920px)");
    this.#desktopMediaQuery.addEventListener(
      "change",
      this.#handleBreakpointChange,
    );

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // If the drawer is torn down (section re-render / editor reorder) while
    // open, clear the modal side effects so the page body isn't left inert
    // or scroll-locked with no dialog present.
    if (this.refs.meganavDrawer?.hasAttribute("data-open")) {
      this.#setBackgroundInert(false);
      document.documentElement.removeAttribute("scroll-lock");
    }
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#groupResizeObserver?.disconnect();
    this.#groupMutationObserver?.disconnect();
    if (this.#timeout) clearTimeout(this.#timeout);
    if (this.#drawerTimeout) clearTimeout(this.#drawerTimeout);
    this.#desktopMediaQuery?.removeEventListener(
      "change",
      this.#handleBreakpointChange,
    );
    document.removeEventListener("scroll", this.#handleWindowScroll);
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}
